/**
 * Wording for bulk import: row statuses, outcomes and the results file.
 * Flow words come from outreach-copy / labels so they match the rest of the
 * portal.
 */
import type { PillTone } from '@/components/ui/pill';
import { INVITE_STAGES, inviteStatusLabel, titleCase, type InviteStageKey } from '@/lib/labels';
import { channelLabel } from '@/lib/outreach-copy';
import { toCsv } from './csv';
import type { ImportRow, RowCheck, RowStatus } from './rows';

export const STATUS_FILTER_LABELS: Record<RowStatus, string> = {
  new: 'New',
  messaged_by_me: 'You messaged them',
  mine: 'Already yours',
  taken: 'Held by someone else',
  invalid: 'Invalid',
  duplicate: 'Duplicate in file',
  unchecked: 'Not checked',
};

export const STATUS_TONES: Record<RowStatus, PillTone> = {
  new: 'info',
  messaged_by_me: 'submitted',
  mine: 'verified',
  taken: 'pending',
  invalid: 'failed',
  duplicate: 'muted',
  unchecked: 'muted',
};

/** "replied" / "PENDING" / "joined" → the stage's portal label. */
export function stageLabel(stage: string | null | undefined): string {
  if (!stage) return '';
  // not_paid → notPaid
  const camel = stage.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  if (camel in INVITE_STAGES) return INVITE_STAGES[camel as InviteStageKey].label;
  const upper = stage.toUpperCase();
  if (['PENDING', 'VERIFIED', 'CONVERTED', 'REJECTED'].includes(upper)) return inviteStatusLabel(upper);
  if (upper === 'CONTACTED') return 'Messaged';
  return titleCase(stage);
}

/** The pill text for one row. */
export function statusText(status: RowStatus, check?: RowCheck, row?: Pick<ImportRow, 'error' | 'rowNumber'> & { duplicateRow?: number }): string {
  switch (status) {
    case 'mine': {
      const stage = stageLabel(check?.inviteStage);
      return stage ? `Already yours (${stage})` : 'Already yours';
    }
    case 'taken':
      return check?.heldByName ? `Held by ${check.heldByName}` : 'Held by someone else';
    case 'invalid': {
      const reason = row?.error ?? check?.reason;
      return reason ? `Invalid (${reason})` : 'Invalid';
    }
    case 'duplicate':
      return row?.duplicateRow ? `Duplicate of row ${row.duplicateRow}` : 'Duplicate in file';
    default:
      return STATUS_FILTER_LABELS[status];
  }
}

/**
 * Extra context under a New / You messaged them pill: an older invite that
 * expired or wasn't paid, or a reason the server may still refuse a log.
 */
export function checkHint(status: RowStatus, check?: RowCheck): string | null {
  if (status !== 'new' && status !== 'messaged_by_me') return null;
  const parts: string[] = [];
  if (check?.inviteStage) parts.push(`Last invite: ${stageLabel(check.inviteStage)}`);
  if (check?.reason) parts.push(check.reason);
  return parts.length > 0 ? parts.join('. ') : null;
}

export type BulkAction = 'messaged' | 'log';

export interface RowOutcome {
  ok: boolean;
  /** Error text, or a note on success. */
  message?: string;
  code?: string;
  invitePublicId?: string;
  /** Not sent: skipped before the request (e.g. already messaged). */
  skipped?: boolean;
}

export const ACTION_LABELS: Record<BulkAction, string> = {
  messaged: 'Marked as messaged',
  log: 'Logged reply',
};

/** The results file: one line per row in the file, with what happened to it. */
export function resultsCsv(
  rows: ReadonlyArray<ImportRow>,
  statusOf: (row: ImportRow) => string,
  outcomes: ReadonlyMap<number, RowOutcome>,
  action: BulkAction,
  origin: string,
): string {
  const header = ['Row', 'Handle', 'Platform', 'Original value', 'Status before', 'Action', 'Outcome', 'Message', 'Invite link'];
  const lines = rows.map((r) => {
    const o = outcomes.get(r.key);
    const outcome = !o ? 'Not sent' : o.skipped ? 'Skipped' : o.ok ? (o.code ? 'OK with a warning' : 'OK') : 'Failed';
    return [
      r.rowNumber,
      r.username ?? '',
      channelLabel(r.channel),
      r.rawHandle,
      statusOf(r),
      o ? ACTION_LABELS[action] : '',
      outcome,
      o?.message ?? '',
      o?.invitePublicId ? `${origin}/portal/invites/${o.invitePublicId}` : '',
    ];
  });
  return toCsv([header, ...lines]);
}

/** Server error codes → plain words. Unknown codes fall back to the server's message. */
export function outcomeMessage(code: string | undefined, message: string | undefined): string {
  switch (code) {
    case 'SCREENSHOT_REQUIRED':
      return 'Your team needs a screenshot to log this. Add one and retry';
    case 'SCREENSHOT_FILE_MISSING':
      return "The screenshot for this row didn't arrive. Retry";
    case 'FILE_TOO_LARGE':
      return 'Screenshot is too large (5 MB max)';
    case 'SCREENSHOT_UPLOAD_FAILED':
      return "Logged, but the screenshot didn't upload. Add it from My creators";
    case 'INVITE_ALREADY_EXISTS':
    case 'DUPLICATE_INVITE':
      return message || 'Already logged. Only one invite per creator is paid';
    case 'CATEGORY_REQUIRED':
      return 'Pick a creator type and retry';
    case 'VALIDATION_ERROR':
      return message ? `Not accepted: ${message}` : 'Not accepted: check the handle, email and niche';
    default:
      return message || (code ? titleCase(code) : 'Failed');
  }
}
